import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDA, fmtDate, todayISO, STATUT_FACTURE, MODES_LABEL } from "@/lib/format";
import { downloadPdf, openFile, downloadExcel } from "@/lib/files";
import { FileUpload } from "@/components/FileUpload";
import { Plus, FileBarChart, Loader2, Wallet, Trash2, FileDown, TrendingUp, Paperclip, Upload, Pencil, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BANK_MODES = ["banque", "virement", "cheque", "carte"];
const emptyLine = () => ({ produit_id: "", designation: "", quantite: 1, prix_unitaire: 0, remise: 0 });

export default function FacturesClients() {
  const { societes, societeParam, societeId, exercice } = useApp();
  const { can, role } = useAuth();
  const [items, setItems] = useState([]);
  const [onlyIncoh, setOnlyIncoh] = useState(false);
  const [totals, setTotals] = useState({ total_reste: 0, total_ttc: 0 });
  const [loading, setLoading] = useState(true);
  const [statut, setStatut] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [clients, setClients] = useState([]);
  const [produits, setProduits] = useState([]);
  const [comptes, setComptes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [editFacId, setEditFacId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [encFor, setEncFor] = useState(null);
  const [enc, setEnc] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importForm, setImportForm] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [journalOpen, setJournalOpen] = useState(false);
  const [jFrom, setJFrom] = useState("");
  const [jTo, setJTo] = useState("");
  const [delFac, setDelFac] = useState(null);

  const doDelete = async () => {
    try {
      await api.delete(`/factures-clients/${delFac.id}`);
      toast.success(`Facture ${delFac.numero} supprimée.`);
      setDelFac(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const genJournal = () => {
    const sid = societeId !== "all" ? societeId : (societes[0]?.id || "");
    if (!sid) { toast.error("Sélectionnez une société."); return; }
    const params = new URLSearchParams({ societe_id: sid });
    if (jFrom) params.append("date_debut", jFrom);
    if (jTo) params.append("date_fin", jTo);
    downloadPdf(`/pdf/journal-ventes?${params.toString()}`, "journal-ventes");
    setJournalOpen(false);
  };

  const printFactures = () => {
    const params = new URLSearchParams();
    if (societeParam.societe_id) params.set("societe_id", societeParam.societe_id);
    if (exercice && exercice !== "all") params.set("annee", exercice);
    if (statut !== "all") params.set("statut", statut);
    if (clientFilter !== "all") params.set("client_id", clientFilter);
    downloadPdf(`/pdf/factures-clients-lot?${params.toString()}`, "factures-clients");
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/factures-clients", { params: {
        ...societeParam,
        statut: statut === "all" ? undefined : statut,
        client_id: clientFilter === "all" ? undefined : clientFilter,
        annee: exercice === "all" ? undefined : exercice,
      } });
      setItems(data.items); setTotals({ total_reste: data.total_reste, total_ttc: data.total_ttc });
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [societeId, statut, clientFilter, exercice]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const loadRefs = (sid) => {
    if (!sid) { setClients([]); setProduits([]); return; }
    api.get("/clients", { params: { societe_id: sid } }).then((r) => setClients(r.data));
    api.get("/produits", { params: { societe_id: sid } }).then((r) => setProduits(r.data));
  };

  const openNew = () => {
    const sid = societeId !== "all" ? societeId : (societes[0]?.id || "");
    setForm({ numero: "", societe_id: sid, client_id: "", client_nom: "", newClientMode: false, date_facture: todayISO(), tva_taux: 19, timbre: 0, timbre_auto: true, mode_paiement: "especes", echeance: todayISO(), decrement_stock: true, piece: null, lignes: [emptyLine()] });
    setEditFacId(null);
    loadRefs(sid);
    setOpen(true);
  };

  const openEdit = (f) => {
    setForm({
      numero: f.numero || "",
      societe_id: f.societe_id, client_id: f.client_id || "", client_nom: "", newClientMode: false,
      date_facture: f.date_facture, tva_taux: f.tva_taux ?? 19, timbre: f.timbre || 0, timbre_auto: false,
      echeance: f.echeance || todayISO(), decrement_stock: false, mode_paiement: f.mode_paiement || "especes", piece: f.piece_id ? { id: f.piece_id, filename: f.piece_nom } : null,
      lignes: (f.lignes || []).map((l) => ({ produit_id: l.produit_id || "", designation: l.designation || "", quantite: l.quantite || 1, prix_unitaire: l.prix_unitaire || 0, remise: l.remise || 0 })),
    });
    setEditFacId(f.id);
    loadRefs(f.societe_id);
    setOpen(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setForm((f) => { const l = [...f.lignes]; l[i] = { ...l[i], [k]: v }; return { ...f, lignes: l }; });
  const pickProduct = (i, pid) => {
    const p = produits.find((x) => x.id === pid);
    setForm((f) => { const l = [...f.lignes]; l[i] = { ...l[i], produit_id: pid, designation: p?.nom || l[i].designation, prix_unitaire: p?.prix_vente || l[i].prix_unitaire }; return { ...f, lignes: l }; });
  };
  const addLine = () => setForm((f) => ({ ...f, lignes: [...f.lignes, emptyLine()] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lignes: f.lignes.filter((_, idx) => idx !== i) }));

  const ht = form ? form.lignes.reduce((s, l) => s + l.quantite * l.prix_unitaire * (1 - (l.remise || 0) / 100), 0) : 0;
  const tva = ht * (form?.tva_taux || 0) / 100;
  const selSoc = societes.find((s) => s.id === form?.societe_id);
  const timbreTaux = selSoc?.timbre_taux ?? 1;
  const timbreCap = selSoc?.timbre_plafond ?? 2500;
  const timbreCalc = Math.min(Math.max(Math.round((ht + tva) * timbreTaux / 100 * 100) / 100, 5), timbreCap);
  const timbre = form?.timbre_auto ? (ht + tva > 0 ? timbreCalc : 0) : (Number(form?.timbre) || 0);

  const openImport = () => {
    const sid = societeId !== "all" ? societeId : (societes[0]?.id || "");
    setImportForm({ societe_id: sid, file: null, mark_paid: false });
    setImportResult(null);
    setImportOpen(true);
  };
  const submitImport = async () => {
    if (!importForm.societe_id) return toast.error("Société requise.");
    if (!importForm.file) return toast.error("Sélectionnez un fichier Excel.");
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importForm.file);
      fd.append("societe_id", importForm.societe_id);
      fd.append("decrement_stock", "false");
      fd.append("mark_paid", importForm.mark_paid ? "true" : "false");
      const { data } = await api.post("/factures-clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`${data.created} facture(s) importée(s).`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setImporting(false); }
  };

  const submit = async () => {
    if (!form.societe_id) return toast.error("Société requise.");
    if (form.newClientMode ? !form.client_nom?.trim() : !form.client_id) return toast.error("Client requis (sélectionnez ou saisissez un nom).");
    if (!form.lignes.length || form.lignes.some((l) => !l.designation)) return toast.error("Chaque ligne doit avoir une désignation.");
    setSaving(true);
    try {
      const payload = { ...form, client_id: form.newClientMode ? null : form.client_id, client_nom: form.newClientMode ? form.client_nom.trim() : null, tva_taux: Number(form.tva_taux) || 0, timbre: timbre, piece_id: form.piece?.id || null, piece_nom: form.piece?.filename || null, lignes: form.lignes.map((l) => ({ ...l, produit_id: l.produit_id || null, quantite: Number(l.quantite), prix_unitaire: Number(l.prix_unitaire), remise: Number(l.remise) || 0 })) };
      if (editFacId) {
        await api.put(`/factures-clients/${editFacId}`, payload);
        toast.success("Facture modifiée.");
      } else {
        await api.post("/factures-clients", payload);
        toast.success("Facture créée.");
      }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const openEnc = (f) => { setEncFor(f); setEnc({ date: todayISO(), montant: f.reste, mode: "especes", compte_id: "", reference: "" }); api.get("/banque/comptes", { params: { societe_id: f.societe_id } }).then((r) => setComptes(r.data)); };
  const submitEnc = async () => {
    if (!enc.montant || Number(enc.montant) <= 0) return toast.error("Montant invalide.");
    try {
      const { data } = await api.post(`/factures-clients/${encFor.id}/encaissements`, { ...enc, montant: Number(enc.montant), compte_id: BANK_MODES.includes(enc.mode) ? enc.compte_id || null : null });
      const r = data.timbre_recalcule;
      toast.success(r ? `Encaissement enregistré — timbre recalculé : ${fmtDA(r.ancien_timbre)} → ${fmtDA(r.timbre)} (TTC ${fmtDA(r.ttc)})` : "Encaissement enregistré.", { duration: r ? 8000 : 4000 });
      setEncFor(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Factures de ventes" subtitle="Chiffre d'affaires et créances" testid="factures-clients-page"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setJournalOpen(true)} className="gap-2" data-testid="journal-ventes-btn"><FileBarChart className="h-4 w-4" /> Journal des ventes (PDF)</Button>
            <Button variant="outline" onClick={printFactures} className="gap-2" data-testid="print-factures-btn"><FileDown className="h-4 w-4" /> Imprimer les factures (PDF)</Button>
            <Button variant="outline" onClick={() => downloadExcel(`/export/ventes${societeId !== "all" ? `?societe_id=${societeId}` : ""}`, "export-ventes")} className="gap-2" data-testid="export-ventes-btn"><FileDown className="h-4 w-4" /> Exporter (Excel)</Button>
            {role === "administrateur" && <Button variant="outline" onClick={() => downloadExcel("/factures-clients/sauvegardes/donut_factures_2025_supprimees.xlsx", "sauvegarde-donut-2025-supprimees")} className="gap-2" data-testid="sauvegarde-donut-2025-btn"><FileDown className="h-4 w-4" /> Sauvegarde 2025</Button>}
            <Button variant="outline" onClick={openImport} className="gap-2" data-testid="import-fc-btn"><Upload className="h-4 w-4" /> Importer un registre Excel</Button>
            {can("ventes") && <Button onClick={openNew} className="gap-2" data-testid="new-fc-btn"><Plus className="h-4 w-4" /> Nouvelle facture</Button>}
          </div>
        } />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 kpi-grid">
        <KpiCard testid="fc-ttc" label="Chiffre d'affaires (liste)" value={totals.total_ttc} icon={TrendingUp} tone="primary" />
        <KpiCard testid="fc-reste" label="Créances (reste à encaisser)" value={totals.total_reste} icon={Wallet} tone="warning" />
        <KpiCard testid="fc-count" label="Nombre de factures" value={items.length} isCurrency={false} icon={FileBarChart} />
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <div className="w-full sm:w-56">
          <Select value={statut} onValueChange={setStatut}>
            <SelectTrigger data-testid="fc-statut-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="non_payee">Non payée</SelectItem>
              <SelectItem value="partiellement_payee">Partiellement payée</SelectItem>
              <SelectItem value="payee">Payée</SelectItem>
              <SelectItem value="en_retard">En retard</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full sm:w-64">
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger data-testid="fc-client-filter"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent className="bg-popover max-h-64">
              <SelectItem value="all">Tous les clients</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.raison_sociale}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {items.some((f) => f.numero_incoherent) && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="fc-numero-alert">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span><b>{items.filter((f) => f.numero_incoherent).length}</b> facture(s) ont un numéro dont le mois ne correspond pas à la date.</span>
          <Button size="sm" variant="outline" className="ml-auto h-8" onClick={() => setOnlyIncoh((v) => !v)} data-testid="fc-numero-alert-toggle">
            {onlyIncoh ? "Afficher toutes les factures" : "Voir ces factures"}
          </Button>
        </div>
      )}

      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          : items.length === 0 ? <EmptyState icon={FileBarChart} title="Aucune facture client" hint="Créez une facture pour suivre le chiffre d'affaires." />
          : (
            <div className="overflow-x-auto">
              <Table data-testid="fc-table">
                <TableHeader><TableRow className="bg-muted/50"><TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead>Date</TableHead><TableHead>Mode</TableHead><TableHead className="text-right">TTC</TableHead><TableHead className="text-right">Reste</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {items.filter((f) => !onlyIncoh || f.numero_incoherent).map((f) => {
                    const st = STATUT_FACTURE[f.statut] || {};
                    return (
                      <TableRow key={f.id} data-testid={`fc-row-${f.id}`}>
                        <TableCell className="tabular text-sm font-medium">
                          <span className="inline-flex items-center gap-1.5">{f.numero}
                            {f.numero_incoherent && <span title="Le mois du numéro ne correspond pas à la date" data-testid={`fc-numero-warn-${f.id}`}><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /></span>}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{f.client_name}</TableCell>
                        <TableCell className="tabular text-sm">{fmtDate(f.date_facture)}</TableCell>
                        <TableCell className="text-sm" data-testid={`fc-mode-${f.id}`}>{f.mode_paiement ? (MODES_LABEL[f.mode_paiement] || f.mode_paiement) : "—"}</TableCell>
                        <TableCell className="tabular text-right">{fmtDA(f.ttc)}</TableCell>
                        <TableCell className="tabular text-right font-semibold">{fmtDA(f.reste)}</TableCell>
                        <TableCell><span className={`text-xs px-2 py-1 rounded-md whitespace-nowrap ${st.cls}`}>{st.label}</span></TableCell>
                        <TableCell>
                          <div className="flex gap-1 justify-end">
                            {f.piece_id && <Button size="icon" variant="ghost" className="h-8 w-8 text-primary" onClick={() => openFile(f.piece_id)} title={f.piece_nom || "Justificatif"} data-testid={`fc-piece-${f.id}`}><Paperclip className="h-4 w-4" /></Button>}
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => downloadPdf(`/pdf/facture-client/${f.id}`, f.numero)} data-testid={`fc-pdf-${f.id}`}><FileDown className="h-4 w-4" /></Button>
                            {role === "administrateur" && f.statut !== "annule" && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(f)} data-testid={`fc-edit-${f.id}`}><Pencil className="h-4 w-4" /></Button>}
                            {f.statut !== "payee" && f.statut !== "annule" && <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => openEnc(f)} data-testid={`enc-btn-${f.id}`}><Wallet className="h-3.5 w-3.5" /> Encaisser</Button>}
                            {role === "administrateur" && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => setDelFac(f)} data-testid={`fc-delete-${f.id}`} title="Supprimer la facture"><Trash2 className="h-4 w-4" /></Button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-popover max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="fc-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editFacId ? "Modifier la facture client" : "Nouvelle facture client"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Société *</Label>
                <Select value={form.societe_id} disabled={!!editFacId} onValueChange={(v) => { setForm((f) => ({ ...f, societe_id: v, client_id: "", lignes: [emptyLine()] })); loadRefs(v); }}>
                  <SelectTrigger data-testid="fc-societe"><SelectValue placeholder="Choisir la société" /></SelectTrigger>
                  <SelectContent>{societes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="space-y-1.5 sm:col-span-2">
                  <div className="flex items-center justify-between">
                    <Label>Client *</Label>
                    <button type="button" className="text-xs text-primary font-medium hover:underline" onClick={() => setField("newClientMode", !form.newClientMode)} data-testid="fc-toggle-newclient">
                      {form.newClientMode ? "← Choisir un client existant" : "+ Nouveau client"}
                    </button>
                  </div>
                  {form.newClientMode ? (
                    <Input placeholder="Nom du nouveau client" value={form.client_nom} onChange={(e) => setField("client_nom", e.target.value)} data-testid="fc-client-nom" />
                  ) : (
                    <Select value={form.client_id} onValueChange={(v) => setField("client_id", v)}>
                      <SelectTrigger data-testid="fc-client"><SelectValue placeholder="Choisir" /></SelectTrigger>
                      <SelectContent className="bg-popover max-h-64">
                        {clients.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Aucun client — utilisez « + Nouveau client »</div>}
                        {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.raison_sociale}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                {role === "administrateur" && <div className="space-y-1.5"><Label>N° facture</Label><Input value={form.numero} onChange={(e) => setField("numero", e.target.value)} placeholder={editFacId ? "" : "Automatique si vide"} data-testid="fc-numero" /></div>}
                <div className="space-y-1.5"><Label>Date</Label><DatePicker value={form.date_facture} onChange={(v) => setField("date_facture", v)} testid="fc-date" /></div>
                <div className="space-y-1.5"><Label>Échéance</Label><DatePicker value={form.echeance} onChange={(v) => setField("echeance", v)} testid="fc-echeance" /></div>
              </div>

              <div className="w-full sm:w-56">
                <Label>Mode de paiement</Label>
                <Select value={form.mode_paiement} onValueChange={(v) => setField("mode_paiement", v)}>
                  <SelectTrigger data-testid="fc-mode-paiement"><SelectValue placeholder="Mode" /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="especes">Espèces</SelectItem>
                    <SelectItem value="cheque">Chèque</SelectItem>
                    <SelectItem value="virement">Virement</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-lg border overflow-hidden">
                <div className="bg-muted/50 px-3 py-2 text-xs font-semibold grid grid-cols-12 gap-2">
                  <span className="col-span-4">Désignation</span><span className="col-span-3">Produit (stock)</span><span className="col-span-1 text-right">Qté</span><span className="col-span-2 text-right">P.U.</span><span className="col-span-1 text-right">Rem.%</span><span className="col-span-1"></span>
                </div>
                {form.lignes.map((l, i) => (
                  <div key={i} className="px-3 py-2 grid grid-cols-12 gap-2 items-center border-t">
                    <Input className="col-span-4 h-9" value={l.designation} onChange={(e) => setLine(i, "designation", e.target.value)} placeholder="Article" data-testid={`fc-line-desig-${i}`} />
                    <div className="col-span-3">
                      <Select value={l.produit_id} onValueChange={(v) => pickProduct(i, v)}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent className="bg-popover max-h-56">{produits.map((p) => <SelectItem key={p.id} value={p.id}>{p.nom}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Input className="col-span-1 h-9 text-right" type="number" value={l.quantite} onChange={(e) => setLine(i, "quantite", e.target.value)} data-testid={`fc-line-qte-${i}`} />
                    <Input className="col-span-2 h-9 text-right" type="number" value={l.prix_unitaire} onChange={(e) => setLine(i, "prix_unitaire", e.target.value)} data-testid={`fc-line-pu-${i}`} />
                    <Input className="col-span-1 h-9 text-right" type="number" value={l.remise} onChange={(e) => setLine(i, "remise", e.target.value)} />
                    <button className="col-span-1 text-muted-foreground hover:text-destructive flex justify-center" onClick={() => removeLine(i)} disabled={form.lignes.length === 1}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <button onClick={addLine} className="w-full text-sm text-primary py-2 border-t hover:bg-accent/40 flex items-center justify-center gap-1.5" data-testid="fc-add-line"><Plus className="h-4 w-4" /> Ajouter une ligne</button>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div className="flex items-end gap-3 flex-wrap">
                  <div className="space-y-1.5 w-24"><Label>TVA (%)</Label><Input type="number" value={form.tva_taux} onChange={(e) => setField("tva_taux", e.target.value)} data-testid="fc-tva" /></div>
                  <div className="space-y-1.5 w-32">
                    <Label>Timbre (DA)</Label>
                    <Input type="number" value={form.timbre_auto ? timbre : form.timbre} onChange={(e) => setField("timbre", e.target.value)} disabled={form.timbre_auto} data-testid="fc-timbre" />
                  </div>
                  <label className="flex items-center gap-2 text-sm mb-2.5" title="1% du HT+TVA, plafonné à 2 500 DA">
                    <input type="checkbox" checked={form.timbre_auto} onChange={(e) => setField("timbre_auto", e.target.checked)} data-testid="fc-timbre-auto" /> Timbre auto (1%)
                  </label>
                  <label className="flex items-center gap-2 text-sm mb-2.5"><input type="checkbox" checked={form.decrement_stock} onChange={(e) => setField("decrement_stock", e.target.checked)} /> Décrémenter le stock</label>
                </div>
                <div className="w-full sm:w-64"><Label className="text-xs">Justificatif</Label><FileUpload value={form.piece} onChange={(v) => setField("piece", v)} testid="fc-piece" /></div>
                <div className="text-right space-y-1 tabular">
                  <p className="text-sm text-muted-foreground">HT : <span className="text-foreground font-medium">{fmtDA(ht)}</span></p>
                  <p className="text-sm text-muted-foreground">TVA : <span className="text-foreground font-medium">{fmtDA(tva)}</span></p>
                  {timbre > 0 && <p className="text-sm text-muted-foreground">Timbre : <span className="text-foreground font-medium">{fmtDA(timbre)}</span></p>}
                  <p className="font-heading text-lg font-bold text-primary">TTC : {fmtDA(ht + tva + timbre)}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} data-testid="fc-submit" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Encaissement */}
      <Dialog open={!!encFor} onOpenChange={(v) => !v && setEncFor(null)}>
        <DialogContent className="bg-popover max-w-md" data-testid="enc-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Encaisser un règlement</DialogTitle></DialogHeader>
          {encFor && enc && (
            <div className="space-y-4">
              <div className="rounded-lg bg-muted/50 p-3 text-sm flex justify-between"><span>{encFor.numero} · {encFor.client_name}</span><span className="tabular font-semibold">Reste {fmtDA(encFor.reste)}</span></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Date</Label><DatePicker value={enc.date} onChange={(v) => setEnc((p) => ({ ...p, date: v }))} testid="enc-date" /></div>
                <div className="space-y-1.5"><Label>Montant *</Label><Input data-testid="enc-montant" type="number" value={enc.montant} onChange={(e) => setEnc((p) => ({ ...p, montant: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label>Mode</Label>
                  <Select value={enc.mode} onValueChange={(v) => setEnc((p) => ({ ...p, mode: v }))}>
                    <SelectTrigger data-testid="enc-mode"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover">{["especes", "banque", "virement", "cheque", "carte"].map((m) => <SelectItem key={m} value={m}>{MODES_LABEL[m]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {BANK_MODES.includes(enc.mode) && (
                  <div className="space-y-1.5"><Label>Compte</Label>
                    <Select value={enc.compte_id} onValueChange={(v) => setEnc((p) => ({ ...p, compte_id: v }))}>
                      <SelectTrigger data-testid="enc-compte"><SelectValue placeholder="Aucun" /></SelectTrigger>
                      <SelectContent className="bg-popover">{comptes.map((c) => <SelectItem key={c.id} value={c.id}>{c.banque} · {c.nom_compte}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEncFor(null)}>Annuler</Button>
            <Button onClick={submitEnc} data-testid="enc-submit">Valider</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Import registre Excel */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-popover max-w-lg" data-testid="import-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Importer un registre Excel</DialogTitle></DialogHeader>
          {importForm && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Chargez un fichier Excel (.xlsx / .xls) contenant vos factures (colonnes : N° facture, Date, Client, Montant HT, TVA, Timbre, TTC). Les clients sont rapprochés automatiquement, les factures en double (même N°) sont ignorées. Importées en non payées, sans impact sur le stock.
              </p>
              <div className="space-y-1.5">
                <Label>Société *</Label>
                <Select value={importForm.societe_id} onValueChange={(v) => setImportForm((f) => ({ ...f, societe_id: v }))}>
                  <SelectTrigger data-testid="import-societe"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    {societes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Fichier Excel *</Label>
                <Input type="file" accept=".xlsx,.xls" onChange={(e) => setImportForm((f) => ({ ...f, file: e.target.files?.[0] || null }))} data-testid="import-file" />
              </div>
              <label className="flex items-center gap-2 text-sm" title="Marque payées les factures dont la colonne de paiement est renseignée (chèque → banque, sinon espèces, à la date de facture).">
                <input type="checkbox" checked={importForm.mark_paid} onChange={(e) => setImportForm((f) => ({ ...f, mark_paid: e.target.checked }))} data-testid="import-mark-paid" />
                Marquer payées selon la colonne « paiement » du fichier
              </label>
              {importResult && (
                <div className="rounded-lg border bg-muted/40 p-3 text-sm space-y-1" data-testid="import-result">
                  <p><strong>{importResult.created}</strong> facture(s) créée(s) · {importResult.skipped} ignorée(s) · {importResult.clients_created} nouveau(x) client(s)</p>
                  <p className="text-muted-foreground">Total TTC importé : {fmtDA(importResult.total_ttc)}</p>
                  {(importResult.paid_cash > 0 || importResult.paid_bank > 0) && (
                    <p className="text-muted-foreground">Encaissements : {importResult.paid_cash} espèces · {importResult.paid_bank} chèque(s)</p>
                  )}
                  {importResult.errors?.length > 0 && <p className="text-destructive text-xs">Erreurs : {importResult.errors.join(" · ")}</p>}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Fermer</Button>
            <Button onClick={submitImport} disabled={importing} data-testid="import-submit" className="gap-2">{importing && <Loader2 className="h-4 w-4 animate-spin" />} Importer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={journalOpen} onOpenChange={setJournalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">Journal des ventes (PDF)</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Document récapitulatif de toutes les factures de ventes avec totaux. Laissez les dates vides pour toute la période.</p>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="space-y-1.5"><Label>Du</Label><DatePicker value={jFrom} onChange={setJFrom} testid="journal-from" /></div>
            <div className="space-y-1.5"><Label>Au</Label><DatePicker value={jTo} onChange={setJTo} testid="journal-to" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJournalOpen(false)}>Annuler</Button>
            <Button onClick={genJournal} data-testid="journal-generate" className="gap-2"><FileBarChart className="h-4 w-4" /> Générer le PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delFac} onOpenChange={(o) => !o && setDelFac(null)}>
        <AlertDialogContent data-testid="fc-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la facture {delFac?.numero} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est définitive. La facture, son bon de livraison lié, les encaissements (caisse/banque) et les mouvements de stock associés seront supprimés et réversés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="fc-delete-confirm">Supprimer définitivement</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
